import { useEffect, useState } from 'react'
import { AlertCircle, Check, Loader2, MapPin, Plus, Save, Search, Tag, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { trpc } from '@/lib/trpc'
import type { CompetitorSettings, City, KeywordType, SearchKeyword } from '@/types'

type CitySettingKey = 'enabledCityIds' | 'competitorEnabledCityIds'

interface VisibilityKeywordsCitiesSettingsProps {
    workspaceId: string
    showKeywords?: boolean
    citySettingKey?: CitySettingKey
    loadCityIds?: () => Promise<number[] | null>
    saveCityIds?: (cityIds: number[] | null) => Promise<void>
    citySectionTitle?: string
    citySectionDescription?: string
    citySaveButtonLabel?: string
}

export function VisibilityKeywordsCitiesSettings({
    workspaceId,
    showKeywords = true,
    citySettingKey = 'enabledCityIds',
    loadCityIds,
    saveCityIds,
    citySectionTitle = 'Enabled Cities',
    citySectionDescription = 'Select geo cities only. Clusters are auto-derived from store mappings in this phase.',
    citySaveButtonLabel = 'Save Cities',
}: VisibilityKeywordsCitiesSettingsProps) {
    const [isLoading, setIsLoading] = useState(true)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const [keywords, setKeywords] = useState<SearchKeyword[]>([])
    const [newKeyword, setNewKeyword] = useState('')
    const [newKeywordType, setNewKeywordType] = useState<KeywordType>('generic')
    const [newKeywordCategory, setNewKeywordCategory] = useState('')
    const [categoryOptions, setCategoryOptions] = useState<string[]>([])
    const [isCategoryTypeaheadOpen, setIsCategoryTypeaheadOpen] = useState(false)
    const [isAddingKeyword, setIsAddingKeyword] = useState(false)

    const [enabledCityIds, setEnabledCityIds] = useState<number[] | null>(null)
    const [selectedCities, setSelectedCities] = useState<City[]>([])
    const [citySearchQuery, setCitySearchQuery] = useState('')
    const [debouncedCityQuery, setDebouncedCityQuery] = useState('')
    const [isCitySearchOpen, setIsCitySearchOpen] = useState(false)
    const [isSavingCities, setIsSavingCities] = useState(false)
    const [citySaveStatus, setCitySaveStatus] = useState<'idle' | 'success' | 'error'>('idle')
    const [hasUnsavedCityChanges, setHasUnsavedCityChanges] = useState(false)

    const utils = trpc.useUtils()

    // tRPC mutations
    const createKeywordMutation = trpc.visibility.createKeyword.useMutation()
    const deleteKeywordMutation = trpc.visibility.deleteKeyword.useMutation()
    const updateVisibilitySettingsMutation = trpc.visibility.updateSettings.useMutation()
    const updateCompetitorSettingsMutation = trpc.competitorScrape.updateSettings.useMutation()

    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true)
                setErrorMessage(null)

                const configuredCityIds = loadCityIds
                    ? await loadCityIds()
                    : await (async () => {
                        if (citySettingKey === 'competitorEnabledCityIds') {
                            const competitorData = await utils.competitorScrape.getSettings.fetch({ workspaceId }) as CompetitorSettings | null
                            return competitorData?.enabledCityIds ?? null
                        }
                        const settingsData = await utils.visibility.settings.fetch({
                            workspaceId,
                        })
                        if (!settingsData) {
                            return null
                        }
                        return settingsData.enabledCityIds
                    })()

                setEnabledCityIds(configuredCityIds)
                if (configuredCityIds && configuredCityIds.length > 0) {
                    const citiesResult = await utils.store.cities.fetch({ ids: configuredCityIds })
                    setSelectedCities(citiesResult.cities)
                } else {
                    setSelectedCities([])
                }

                if (showKeywords) {
                    const [keywordsData, l1Result, l2Result] = await Promise.all([
                        utils.visibility.getKeywords.fetch({ workspaceId }),
                        utils.categories.list.fetch({ workspaceId, platform: 'blinkit', level: 1 }),
                        utils.categories.list.fetch({ workspaceId, platform: 'blinkit', level: 2 }),
                    ])

                    setKeywords(keywordsData || [])

                    // Extract category names from results
                    const l1Categories = (l1Result || [])
                        .map((c: { name?: string }) => c.name?.trim())
                        .filter((n: string | undefined): n is string => Boolean(n))
                    const l2Categories = (l2Result || [])
                        .map((c: { name?: string }) => c.name?.trim())
                        .filter((n: string | undefined): n is string => Boolean(n))
                    const l1Unique = Array.from(new Set(l1Categories)).sort((a: string, b: string) => a.localeCompare(b))
                    const l2Unique = Array.from(new Set(l2Categories)).sort((a: string, b: string) => a.localeCompare(b))

                    const keywordCategories = (keywordsData || [])
                        .map((keyword: SearchKeyword) => keyword.category?.trim())
                        .filter((category: string | undefined | null): category is string => Boolean(category))
                    const mergedCategoryOptions = Array.from(
                        new Set([...keywordCategories, ...l1Unique, ...l2Unique])
                    ).sort((a, b) =>
                        a.localeCompare(b)
                    )
                    setCategoryOptions(mergedCategoryOptions)
                }
                setHasUnsavedCityChanges(false)
            } catch (error) {
                console.error('Error loading visibility keyword/city settings:', error)
                setErrorMessage(showKeywords ? 'Failed to load keyword and city settings' : 'Failed to load city settings')
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [workspaceId, citySettingKey, showKeywords, loadCityIds, utils])

    // Debounce city search query
    useEffect(() => {
        if (citySearchQuery.trim().length < 2) {
            setDebouncedCityQuery('')
            return
        }

        const timer = setTimeout(() => {
            setDebouncedCityQuery(citySearchQuery)
        }, 300)

        return () => clearTimeout(timer)
    }, [citySearchQuery])

    // Fetch city search results via tRPC
    const { data: citySearchData, isLoading: isCitySearchLoading } = trpc.store.cities.useQuery(
        { query: debouncedCityQuery },
        { enabled: debouncedCityQuery.length >= 2 }
    )

    // Filter out already-selected cities from search results
    const citySearchResults = (citySearchData?.cities ?? []).filter(
        (city) => !selectedCities.some((sc) => sc.id === city.id)
    )

    const handleAddKeyword = async () => {
        if (!newKeyword.trim()) return
        if (!newKeywordCategory.trim()) {
            setErrorMessage('Please select a product category')
            return
        }

        if (!categoryOptions.some((category) => category.toLowerCase() === newKeywordCategory.trim().toLowerCase())) {
            setErrorMessage('Please select a valid category from suggestions')
            return
        }

        try {
            setIsAddingKeyword(true)
            setErrorMessage(null)

            const keyword = await createKeywordMutation.mutateAsync({
                workspaceId,
                keyword: newKeyword.trim(),
                keywordType: newKeywordType,
                category: newKeywordCategory,
            })

            if (keyword && keyword.keywordType) {
                setKeywords((prev) => [...prev, keyword])
                if (keyword.category) {
                    setCategoryOptions((prev) =>
                        Array.from(new Set([...prev, keyword.category!])).sort((a, b) => a.localeCompare(b))
                    )
                }
                setNewKeyword('')
                setNewKeywordType('generic')
                setNewKeywordCategory('')
                setIsCategoryTypeaheadOpen(false)
            } else {
                setErrorMessage('Failed to add keyword - invalid response')
            }
        } catch (error) {
            console.error('Error adding keyword:', error)
            setErrorMessage(error instanceof Error ? error.message : 'Failed to add keyword')
        } finally {
            setIsAddingKeyword(false)
        }
    }

    const handleDeleteKeyword = async (keywordId: string) => {
        try {
            await deleteKeywordMutation.mutateAsync({
                workspaceId,
                keywordId,
            })
            setKeywords((prev) => prev.filter((keyword) => keyword.id !== keywordId))
        } catch (error) {
            console.error('Error deleting keyword:', error)
            setErrorMessage('Failed to delete keyword')
        }
    }

    const handleAddCity = (city: City) => {
        setSelectedCities((prev) => {
            if (prev.some((selectedCity) => selectedCity.id === city.id)) {
                return prev
            }
            return [...prev, city]
        })

        setEnabledCityIds((prev) => {
            if (prev === null) {
                return [city.id]
            }
            if (prev.includes(city.id)) {
                return prev
            }
            return [...prev, city.id]
        })

        setHasUnsavedCityChanges(true)
        setCitySearchQuery('')
        setDebouncedCityQuery('')
        setIsCitySearchOpen(false)
    }

    const handleRemoveCity = (cityId: number) => {
        setSelectedCities((prev) => prev.filter((city) => city.id !== cityId))
        setEnabledCityIds((prev) => {
            if (prev === null) {
                return []
            }
            return prev.filter((id) => id !== cityId)
        })
        setHasUnsavedCityChanges(true)
    }

    const toggleAllCities = () => {
        if (enabledCityIds === null) {
            setEnabledCityIds([])
        } else {
            setEnabledCityIds(null)
            setSelectedCities([])
        }
        setHasUnsavedCityChanges(true)
    }

    const handleSaveCities = async () => {
        try {
            setIsSavingCities(true)
            setErrorMessage(null)

            if (saveCityIds) {
                await saveCityIds(enabledCityIds)
            } else if (citySettingKey === 'competitorEnabledCityIds') {
                await updateCompetitorSettingsMutation.mutateAsync({
                    workspaceId,
                    enabledCityIds: enabledCityIds,
                })
            } else {
                await updateVisibilitySettingsMutation.mutateAsync({
                    workspaceId,
                    [citySettingKey]: enabledCityIds,
                })
            }

            setCitySaveStatus('success')
            setHasUnsavedCityChanges(false)
            setTimeout(() => setCitySaveStatus('idle'), 3000)
        } catch (error) {
            console.error('Error saving enabled cities:', error)
            setErrorMessage(error instanceof Error ? error.message : 'Failed to save enabled cities')
            setCitySaveStatus('error')
            setTimeout(() => setCitySaveStatus('idle'), 3000)
        } finally {
            setIsSavingCities(false)
        }
    }

    const getKeywordsByType = (type: KeywordType) => keywords.filter((keyword) => keyword && keyword.keywordType === type)
    const filteredCategoryOptions = categoryOptions
        .filter((category) => category.toLowerCase().includes(newKeywordCategory.trim().toLowerCase()))
        .slice(0, 12)

    const allCitiesEnabled = enabledCityIds === null

    if (isLoading) {
        return (
            <Card>
                <CardContent className="p-6 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </CardContent>
            </Card>
        )
    }

    return (
        <div className="space-y-6">
            {showKeywords && (
                <Card>
                    <CardHeader>
                        <div className="flex items-center gap-3">
                            <Tag className="h-5 w-5 text-primary" />
                            <div>
                                <CardTitle className="text-lg">Keywords</CardTitle>
                                <CardDescription>Manage search keywords for SOV tracking</CardDescription>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-4">
                            <Label>Add New Keyword</Label>
                            <div className="flex flex-col md:flex-row gap-2">
                                <Input
                                    placeholder="Add keyword"
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && newKeyword.trim()) {
                                            handleAddKeyword()
                                        }
                                    }}
                                    className="flex-1"
                                />
                                <div className="relative md:w-[280px]">
                                    <Input
                                        placeholder="Select category"
                                        value={newKeywordCategory}
                                        onChange={(e) => {
                                            setNewKeywordCategory(e.target.value)
                                            setIsCategoryTypeaheadOpen(true)
                                        }}
                                        onFocus={() => setIsCategoryTypeaheadOpen(true)}
                                    />
                                    {isCategoryTypeaheadOpen && newKeywordCategory.trim().length > 0 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md z-50 max-h-[280px] overflow-auto">
                                            {filteredCategoryOptions.length > 0 ? (
                                                <div className="p-1">
                                                    {filteredCategoryOptions.map((categoryName) => (
                                                        <button
                                                            key={categoryName}
                                                            onClick={() => {
                                                                setNewKeywordCategory(categoryName)
                                                                setIsCategoryTypeaheadOpen(false)
                                                            }}
                                                            className="w-full px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground text-left"
                                                        >
                                                            {categoryName}
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="p-3 text-sm text-muted-foreground">No matching categories</div>
                                            )}
                                        </div>
                                    )}
                                    {isCategoryTypeaheadOpen && (
                                        <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setIsCategoryTypeaheadOpen(false)} />
                                    )}
                                </div>
                                <Select value={newKeywordType} onValueChange={(value) => setNewKeywordType(value as KeywordType)}>
                                    <SelectTrigger className="md:w-[180px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="generic">Generic</SelectItem>
                                        <SelectItem value="brand">Brand</SelectItem>
                                        <SelectItem value="competition">Competition</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Button onClick={handleAddKeyword} disabled={!newKeyword.trim() || !newKeywordCategory.trim() || isAddingKeyword}>
                                    {isAddingKeyword ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                                </Button>
                            </div>
                            {categoryOptions.length === 0 && (
                                <p className="text-sm text-muted-foreground">No categories available yet. Trigger category scraping first.</p>
                            )}
                        </div>

                        <div className="space-y-4">
                            <div>
                                <div className="text-sm font-medium text-muted-foreground mb-2">
                                    Generic Keywords ({getKeywordsByType('generic').length})
                                </div>
                                {getKeywordsByType('generic').length === 0 ? (
                                    <div className="p-4 text-center border rounded-lg bg-muted/10">
                                        <p className="text-sm text-muted-foreground">No generic keywords</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {getKeywordsByType('generic').map((keyword) => (
                                            <div
                                                key={keyword.id}
                                                className="flex items-center gap-2 px-3 py-1.5 border rounded-md bg-background group hover:border-primary/50 transition-colors"
                                            >
                                                <span className="text-sm font-medium">{keyword.keyword}</span>
                                                {keyword.category && (
                                                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{keyword.category}</span>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteKeyword(keyword.id)}
                                                    className="text-muted-foreground hover:text-destructive transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="text-sm font-medium text-muted-foreground mb-2">
                                    Brand Keywords ({getKeywordsByType('brand').length})
                                </div>
                                {getKeywordsByType('brand').length === 0 ? (
                                    <div className="p-4 text-center border rounded-lg bg-muted/10">
                                        <p className="text-sm text-muted-foreground">No brand keywords</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {getKeywordsByType('brand').map((keyword) => (
                                            <div
                                                key={keyword.id}
                                                className="flex items-center gap-2 px-3 py-1.5 border rounded-md bg-blue-50 border-blue-200 group hover:border-blue-400 transition-colors"
                                            >
                                                <span className="text-sm font-medium text-blue-900">{keyword.keyword}</span>
                                                {keyword.category && (
                                                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{keyword.category}</span>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteKeyword(keyword.id)}
                                                    className="text-blue-600 hover:text-destructive transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <div className="text-sm font-medium text-muted-foreground mb-2">
                                    Competition Keywords ({getKeywordsByType('competition').length})
                                </div>
                                {getKeywordsByType('competition').length === 0 ? (
                                    <div className="p-4 text-center border rounded-lg bg-muted/10">
                                        <p className="text-sm text-muted-foreground">No competition keywords</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {getKeywordsByType('competition').map((keyword) => (
                                            <div
                                                key={keyword.id}
                                                className="flex items-center gap-2 px-3 py-1.5 border rounded-md bg-red-50 border-red-200 group hover:border-red-400 transition-colors"
                                            >
                                                <span className="text-sm font-medium text-red-900">{keyword.keyword}</span>
                                                {keyword.category && (
                                                    <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-800">{keyword.category}</span>
                                                )}
                                                <button
                                                    onClick={() => handleDeleteKeyword(keyword.id)}
                                                    className="text-red-600 hover:text-destructive transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <MapPin className="h-5 w-5 text-primary" />
                            <div>
                                <CardTitle className="text-lg">{citySectionTitle}</CardTitle>
                                <CardDescription>{citySectionDescription}</CardDescription>
                            </div>
                        </div>
                        <Button onClick={handleSaveCities} disabled={isSavingCities || !hasUnsavedCityChanges}>
                            {isSavingCities ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Saving...
                                </>
                            ) : citySaveStatus === 'success' ? (
                                <>
                                    <Check className="h-4 w-4 mr-2" />
                                    Saved!
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    {citySaveButtonLabel}
                                </>
                            )}
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 border-2 border-dashed rounded-lg bg-muted/30">
                            <div>
                                <span className="font-medium">All Cities</span>
                                <p className="text-sm text-muted-foreground">Enable scraping for all available cities</p>
                            </div>
                            <Switch checked={allCitiesEnabled} onCheckedChange={toggleAllCities} />
                        </div>

                        {!allCitiesEnabled && (
                            <div className="space-y-4">
                                <div className="relative">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Search to add cities (e.g. Bangalore, Mumbai)..."
                                            value={citySearchQuery}
                                            onChange={(e) => {
                                                setCitySearchQuery(e.target.value)
                                                if (!isCitySearchOpen) setIsCitySearchOpen(true)
                                            }}
                                            onFocus={() => setIsCitySearchOpen(true)}
                                            className="pl-9"
                                        />
                                        {isCitySearchLoading && (
                                            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-primary animate-spin" />
                                        )}
                                    </div>

                                    {isCitySearchOpen && citySearchQuery.length >= 2 && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md z-50 max-h-[300px] overflow-auto">
                                            {citySearchResults.length > 0 ? (
                                                <div className="p-1">
                                                    {citySearchResults.map((city) => (
                                                        <button
                                                            key={city.id}
                                                            onClick={() => handleAddCity(city)}
                                                            className="w-full flex items-center justify-between px-3 py-2 text-sm rounded-sm hover:bg-accent hover:text-accent-foreground text-left"
                                                        >
                                                            <span>{city.name}</span>
                                                            <Check className="h-4 w-4 opacity-0" />
                                                        </button>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="p-4 text-center text-sm text-muted-foreground">
                                                    {isCitySearchLoading ? 'Searching...' : 'No cities found'}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {isCitySearchOpen && (
                                        <div className="fixed inset-0 z-40 bg-transparent" onClick={() => setIsCitySearchOpen(false)} />
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <div className="text-sm font-medium text-muted-foreground">Selected Cities ({selectedCities.length})</div>

                                    {selectedCities.length === 0 ? (
                                        <div className="p-8 text-center border rounded-lg bg-muted/10">
                                            <MapPin className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                                            <p className="text-sm text-foreground">No cities selected</p>
                                            <p className="text-xs text-muted-foreground">Search and add cities to enable scraping</p>
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                            {selectedCities.map((city) => (
                                                <div
                                                    key={city.id}
                                                    className="flex items-center justify-between p-2 px-3 border rounded-md bg-background group hover:border-primary/50 transition-colors"
                                                >
                                                    <span className="text-sm font-medium">{city.name}</span>
                                                    <button
                                                        onClick={() => handleRemoveCity(city.id)}
                                                        className="text-muted-foreground hover:text-destructive transition-colors p-1 rounded-full hover:bg-destructive/10"
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {errorMessage && (
                <Card className="border-destructive bg-destructive/5">
                    <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                            <p className="text-sm font-medium text-destructive">{errorMessage}</p>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    )
}
